// 261010 UI2 (L2) — ô engine / model / độ phân giải của MỘT video, ở ngăn Sửa bước Video (`PUT /events/:id/generation`).
// 2.0: model + độ phân giải; 2.5: chỉ độ phân giải. Có xác nhận (đổi ⇒ clip cũ thành "cũ"); KHÔNG giá. Logic thuần ở workflowGenerationUtils; NEEDS_SPLIT hiện thành ghi chú (máy tự chia ở Đạo diễn).
import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Popconfirm, Select, Space, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { FilmEvent } from '../../types/film';
import type { FilmRunningJob } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import {
  GENERATION_ENGINES, GENERATION_MODELS_20, GENERATION_RESOLUTIONS, generationBodyOf, generationErrorText, generationFormFixOf, generationFormOf, generationLabelOf, sameGeneration, type GenerationForm,
} from './workflowGenerationUtils';
import { needsSplitOf, splitNoteOf } from './workflowManyUtils';

interface Props { eventId: string; eventOrder?: number; ev: FilmEvent; jobs: FilmRunningJob[]; onActionDone: () => void }

const WorkflowGenerationEditor: React.FC<Props> = ({ eventId, eventOrder, ev, jobs, onActionDone }) => {
  const alive = useAliveRef();
  const g = ev.generation ?? null;
  const [form, setForm] = useState<GenerationForm>(() => generationFormOf(g));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setForm(generationFormOf(ev.generation)); setErr(null); }, [eventId, ev.generation?.revision, ev.generation?.engine, ev.generation?.model, ev.generation?.resolution]); // eslint-disable-line react-hooks/exhaustive-deps

  const split = needsSplitOf(ev);
  const body = generationBodyOf(form);
  const dirty = !!g && !sameGeneration(form, generationFormOf(g));
  const running = jobs.length > 0;
  const why = !g ? 'Plan này chưa chọn engine lúc tạo — chỉ xem.' : running ? 'Plan đang có việc chạy ở nền — đợi xong rồi đổi.' : body.ok === false ? body.message : !dirty ? 'Chưa đổi gì.' : null;
  const name = `Video ${eventOrder ?? ''}`.trim();

  const save = async (acceptEditedPrompt = false): Promise<void> => {
    if (body.ok === false) return;
    setBusy(true); setErr(null);
    try {
      await filmApi.setEventGeneration(eventId, { ...body.body, ...(acceptEditedPrompt ? { acceptEditedPrompt: true } : {}) });
      message.success('Đã đổi engine — clip cũ của video thành "cũ", tạo lại khi cần.');
      onActionDone();
    } catch (e: any) {
      if (e?.response?.data?.code === 'PROMPT_EDITED_TIMING') {
        Modal.confirm({
          title: 'Giữ nguyên prompt đã sửa tay?', content: 'Prompt Kỹ thuật này đã sửa tay và mang mốc giây của engine cũ. Giữ nguyên rồi đổi engine, hay huỷ để viết lại prompt trước?',
          okText: 'Giữ nguyên và đổi', cancelText: 'Huỷ', onOk: () => save(true),
        });
      } else if (alive.current) setErr(generationErrorText(e, 'Đổi engine'));
    } finally { if (alive.current) setBusy(false); }
  };

  return (
    <section className="wf-gen" data-testid="wf-gen-editor" aria-label="Engine của video">
      <div className="wf-gen-h"><b>Engine</b><span className="mono">{g ? generationLabelOf(g) : ev.videoEngine === 'SEEDANCE_25' ? '2.5' : ev.videoEngine === 'SEEDANCE_20' ? '2.0' : 'chưa chọn'}</span>
        {g?.source === 'HUMAN' ? <span className="wf-chip todo" title="Video này được đổi riêng, không theo cấu hình lúc tạo plan">riêng</span> : null}</div>
      <Space wrap size={8}>
        <Select size="small" style={{ width: 130 }} value={form.engine} disabled={!g || busy} options={GENERATION_ENGINES.map((e) => ({ value: e.value, label: e.label }))}
          onChange={(v) => setForm((f) => generationFormFixOf({ ...f, engine: v }))} data-testid="wf-gen-engine" />
        {form.engine === 'SEEDANCE_20'
          ? <Select size="small" style={{ width: 110 }} value={form.model ?? 'mini'} disabled={!g || busy} options={GENERATION_MODELS_20.map((m) => ({ value: m.value, label: m.label }))} onChange={(v) => setForm((f) => ({ ...f, model: v }))} data-testid="wf-gen-model" />
          : <span className="muted" style={{ fontSize: 12 }}>2.5: chưa có lựa chọn model</span>}
        <Select size="small" style={{ width: 96 }} value={form.resolution ?? '480p'} disabled={!g || busy} options={GENERATION_RESOLUTIONS.map((r) => ({ value: r.value, label: r.label }))} onChange={(v) => setForm((f) => ({ ...f, resolution: v }))} data-testid="wf-gen-res" />
        <Popconfirm title={`Đổi engine của ${name}?`} description={<div style={{ maxWidth: 300 }}>{`Sang ${generationLabelOf(form)}. Clip đã tạo của video này sẽ thành "cũ" (tạo lại khi cần).`}</div>} okText="Đổi" cancelText="Huỷ" disabled={!!why} onConfirm={() => { void save(); }}>
          <Button size="small" type="primary" loading={busy} disabled={!!why} title={why ?? undefined} data-testid="wf-gen-save">Lưu engine</Button>
        </Popconfirm>
      </Space>
      {dirty && g ? <div className="muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="wf-gen-unsaved">Chưa lưu — tạo clip vẫn dùng cấu hình đã lưu ({generationLabelOf(g)}).</div> : null}
      {why && g ? <div className="muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="wf-gen-why">{why}</div> : null}
      {split ? <Alert type="warning" showIcon style={{ marginTop: 8 }} message={splitNoteOf(split)} data-testid="wf-gen-split" /> : null}
      {err ? <Alert type="error" showIcon style={{ marginTop: 8 }} message={err} closable onClose={() => setErr(null)} data-testid="wf-gen-error" /> : null}
    </section>
  );
};

export default WorkflowGenerationEditor;
